import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Button, Tab, Tabs } from "@mui/material";
import React, { useState, useEffect } from "react";
import ViewDocModal from "../../Modals/viewDocModal";
import PropertyList from "../../features/manageCustomers/propertyList";
import { useLazyGetPortfolioByIdQuery } from "../../services/users";
import { SKILLS, INDUSTRY, MEDIA_TYPE } from "../../utils/enums";
import Loader from "../../constants/Loader";

interface PortfolioData {
  userId?: string;
  title?: string;
  duration?: number;
  industry?: number[];
  skills?: number[];
  intro?: string;
  coverImage?: string;
  media?: Array<{
    _id: string;
    link: string;
    mediaType: number;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const PortfolioDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [getPortfolioById] = useLazyGetPortfolioByIdQuery();
  const [portfolioData, setPortfolioData] = useState<PortfolioData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    type: string;
  } | null>(null);

  const fetchPortfolioDetails = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await getPortfolioById({ ProjectId: id }).unwrap();
      if (response?.statusCode === 200) {
        setPortfolioData(response?.data || null);
      }
    } catch (error) {
      console.error("Error fetching portfolio details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolioDetails();
  }, [id]);

  const handleMediaClick = (mediaUrl: string, mediaType: string) => {
    setSelectedMedia({ url: mediaUrl, type: mediaType });
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedMedia(null);
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <MainContainer>
        <div className="main_title">
          <h1>Freelancer Project Detail</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/manage-freelancer")}>
              Manage Freelancer
            </span>
            -{" "}
            <span
              onClick={() =>
                navigate(
                  `/manage-freelancer/detail/${portfolioData?.userId}?page=1`
                )
              }
            >
              Freelancer Detail
            </span>{" "}
            - Portfolio Detail
          </p>
        </div>

        <div className="cards view_page">
          <div className="section_title">
            <h2>Portfolio Details</h2>
          </div>
          {portfolioData ? (
            <div className="gap_p">
              <div className="w_33">
                <h6>Title</h6>
                <p>{portfolioData?.title || "-"}</p>
              </div>
              <div className="w_33">
                <h6>Duration</h6>
                <p>
                  <strong>
                    {portfolioData?.duration
                      ? `${portfolioData?.duration} months`
                      : "-"}
                  </strong>
                </p>
              </div>
              <div className="w_33">
                <h6>Industry</h6>
                <p>
                  {Array.isArray(portfolioData?.industry)
                    ? portfolioData?.industry
                        .map((industryId: number) => INDUSTRY[industryId])
                        .filter(Boolean)
                        .join(", ")
                    : "-"}
                </p>
              </div>
              <div className="w_100">
                <h6>Skills</h6>
                <p>
                  {Array.isArray(portfolioData?.skills)
                    ? portfolioData?.skills
                        .map((skillId: number) => SKILLS[skillId])
                        .join(", ")
                    : "-"}
                </p>
              </div>

              <div className="w_100">
                <h6>Intro</h6>
                <p>{portfolioData?.intro || "-"}</p>
              </div>

              <div className="w_50">
                <h6>Cover Image</h6>
                {portfolioData?.coverImage ? (
                  <figure
                    className="passport_img"
                    style={{ height: "200px", overflow: "hidden", cursor: "pointer" }}
                    onClick={() => handleMediaClick(portfolioData.coverImage || "", "image")}
                  >
                    <img
                      src={portfolioData?.coverImage}
                      alt="Cover"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.currentTarget.src = "/static/images/placeholder.png";
                      }}
                    />
                  </figure>
                ) : (
                  <p>No cover image available</p>
                )}
              </div>

              <div className="w_100">
                <h6>Media</h6>
                {portfolioData?.media && portfolioData?.media.length > 0 ? (
                  <div
                    className="gap_p"
                    style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}
                  >
                    {portfolioData?.media?.map((item) => (
                      <div
                        key={item._id}
                        className="w_33"
                        style={{
                          width: "calc(33.33% - 14px)",
                          cursor: "pointer",
                          transition: "transform 0.2s",
                          transform: "scale(1)",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "scale(1.02)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                        onClick={() =>
                          handleMediaClick(
                            item.link,
                            item.mediaType === MEDIA_TYPE.IMAGE
                              ? "image"
                              : "video"
                          )
                        }
                      >
                        <figure
                          className="passport_img"
                          style={{ height: "200px", overflow: "hidden" }}
                        >
                          {item.mediaType === MEDIA_TYPE.IMAGE ? (
                            <img
                              src={item.link}
                              alt="Portfolio media"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <video
                              src={item.link}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </figure>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>There is No media</p>
                )}
              </div>
            </div>
          ) : (
            <div>No portfolio data found</div>
          )}
        </div>
      </MainContainer>

      <ViewDocModal
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
        title={selectedMedia?.type === "image" && selectedMedia?.url === portfolioData?.coverImage ? "Cover Image View" : "Media Preview"}
      >
        {selectedMedia && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            {selectedMedia?.type === "image" ? (
              <img
                src={selectedMedia?.url}
                alt="Media preview"
                style={{
                  width: "100%",
                  height: "80vh",
                  objectFit: "contain",
                  maxWidth: "100%",
                }}
              />
            ) : (
              <video
                src={selectedMedia?.url}
                controls
                style={{
                  width: "100%",
                  height: "80vh",
                  maxWidth: "100%",
                }}
              />
            )}
          </div>
        )}
      </ViewDocModal>
    </>
  );
};

export default PortfolioDetails;
